import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { ACCESS_TOKEN_KEY, clearSession, getAccessToken, redirectToLogin } from './session';

interface AuthContextType {
  token: string | null;
  setToken: (token: string | null) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [token, setTokenState] = useState<string | null>(getAccessToken);

  const setToken = (newToken: string | null) => {
    setTokenState(newToken);
    if (newToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, newToken);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  };

  const logout = () => {
    setTokenState(null);
    clearSession();
    redirectToLogin();
  };

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ token, setToken, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
